/**
 * The client side of the chat prepare call. When the user reaches for the
 * composer, the backend is asked to get the conversation ready for its next
 * message, so the send that follows starts sooner.
 *
 * A chat that does not exist yet has no id to prepare, so this window chooses
 * one and announces it. The first message of that chat is then sent under the
 * announced id. Until the backend has confirmed a preparation for it, the id is
 * never sent with a message, and the backend picks the id as it always has.
 */

export type TurnPreparationTarget = {
  conversationId: string
  /** The id was chosen here for a chat that has not been sent to yet. */
  newConversation: boolean
}

export type TurnPreparationReply = {
  prepared: boolean
  /** How long the backend holds a prepared turn. */
  ttlMs: number
}

/** Resolves null when the call failed. It never rejects. */
export type RequestTurnPreparation = (target: TurnPreparationTarget) => Promise<TurnPreparationReply | null>

/**
 * How long to leave a conversation alone after asking, until the backend says
 * how long it holds a prepared turn. Also the spacing after a failed call, so
 * a backend that is down is not asked on every keystroke.
 */
const UNCONFIRMED_SPACING_MS = 15_000

type TurnPreparationDeps = {
  mintConversationId: () => string
  now: () => number
}

export function createTurnPreparation({ mintConversationId, now }: TurnPreparationDeps) {
  // One id for the next new chat, kept until a send takes it, so a chat that
  // is never sent leaves at most one id announced to the backend.
  let newChat: { id: string; confirmed: boolean } | undefined
  const leaveAloneUntil = new Map<string, number>()

  const forgetElapsedSpacings = () => {
    for (const [conversationId, until] of leaveAloneUntil) {
      if (now() >= until) {
        leaveAloneUntil.delete(conversationId)
      }
    }
  }

  return {
    /**
     * Asks the backend to prepare the next message to `chatId`, or to a new
     * chat when there is none. Safe to call on every focus and keystroke:
     * calls for a conversation that was prepared recently do nothing.
     */
    prepare(chatId: string | undefined, request: RequestTurnPreparation): void {
      forgetElapsedSpacings()
      const conversationId = chatId ?? (newChat ??= { id: mintConversationId(), confirmed: false }).id
      if (leaveAloneUntil.has(conversationId)) {
        return
      }

      const askedAt = now()
      leaveAloneUntil.set(conversationId, askedAt + UNCONFIRMED_SPACING_MS)
      // Not awaited. Nothing the user does waits on a prepare call.
      void request({ conversationId, newConversation: chatId === undefined }).then((reply) => {
        if (!reply) {
          return
        }
        // Ask again halfway through the hold, so a user who keeps typing always has one ready.
        leaveAloneUntil.set(conversationId, askedAt + reply.ttlMs / 2)
        if (reply.prepared && newChat?.id === conversationId) {
          newChat.confirmed = true
        }
      })
    },

    /**
     * The id the backend has prepared the next new chat under, for the send
     * that opens it. Undefined when no preparation was confirmed. Each id is
     * handed out once.
     */
    takeNewChatId(): string | undefined {
      if (!newChat?.confirmed) {
        return undefined
      }
      const { id } = newChat
      newChat = undefined
      leaveAloneUntil.delete(id)
      return id
    },
  }
}

export type TurnPreparation = ReturnType<typeof createTurnPreparation>
